import React, { useEffect, useMemo } from 'react';
import {
  DefaultMeshBus,
  WebSocketLinkAdapter,
  BroadcastChannelLink,
  DirectoryService,
  BroadcastService,
  InviteService,
  ChatIngestionService,
  globalVaultDB,
} from 'mesh-microkernel';
import { AppShell } from './shell/AppShell';
import { HealthDashboard } from './shell/HealthDashboard';
import { notificationService, type SystemNotification } from './core/events/NotificationService';
import { GuestPortal } from './shell/guest/GuestPortal';
import { useInviteParam } from './shell/guest/useInviteParam';


const SystemNotificationToast: React.FC = () => {
  const [currentNotif, setCurrentNotif] = React.useState<SystemNotification | null>(null);

  React.useEffect(() => {
    const unsubscribe = notificationService.subscribe((notif) => {
      setCurrentNotif(notif);
      // auto-dismiss after 5 seconds
      setTimeout(() => setCurrentNotif((prev) => prev?.id === notif.id ? null : prev), 5000);
    });
    return unsubscribe;
  }, []);

  if (!currentNotif) return null;

  const bgColors = {
    info: 'bg-blue-600',
    warn: 'bg-yellow-600',
    error: 'bg-red-600',
    alert: 'bg-purple-600'
  };

  return (
    <div className={`fixed top-4 right-4 z-50 p-4 rounded shadow-lg text-white ${bgColors[currentNotif.level] || bgColors.info} transition-opacity duration-300`}>
      <h4 className="font-bold">{currentNotif.title} <span className="text-sm opacity-75">({currentNotif.moduleId})</span></h4>
      <p>{currentNotif.message}</p>
      <button onClick={() => setCurrentNotif(null)} className="absolute top-2 right-2 opacity-50 hover:opacity-100">&times;</button>
    </div>
  );
};

// Route to HealthDashboard when the URL hash is #/health (set by the tray "Open Health Dashboard" menu item)
function isHealthRoute() {
  return window.location.hash === '#/health';
}

interface AppProps {
  guestSession?: {
    sessionId: string;
    token: string;
    displayName: string;
    roomId: string;
  } | null;
}

export const App: React.FC<AppProps> = ({ guestSession }) => {
  // Show health dashboard in the dedicated health window opened by tray
  if (isHealthRoute()) {
    return <HealthDashboard />;
  }

  const inviteToken = useInviteParam();
  const [admittedGuest, setAdmittedGuest] = React.useState<{ sessionId: string; token: string; displayName: string } | null>(null);

  if (inviteToken && !admittedGuest) {
    return <GuestPortal inviteToken={inviteToken} onAdmitted={setAdmittedGuest} />;
  }

  return (
    <>
      <SystemNotificationToast />
      <MeshApp />
    </>
  );
};

const MeshApp: React.FC = () => {
  const nodeId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const peerParam = params.get('peer');
    if (peerParam) return peerParam;

    const storedNodeId = localStorage.getItem('mesh_node_id');
    if (storedNodeId) return storedNodeId;

    const newId = `peer-${Math.random().toString(36).slice(2, 7)}`;
    localStorage.setItem('mesh_node_id', newId);
    return newId;
  }, []);

  const { directory, broadcast, invite, ingestion } = useMemo(() => {
    const bus = new DefaultMeshBus();
    const getSignalingEndpoint = (): string => {
      const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
      const relayParam = urlParams?.get('relay');
      const inviteParam = urlParams?.get('invite') || urlParams?.get('token');
      if (relayParam) {
        const wsProto = relayParam.startsWith('https:') ? 'wss:' : 'ws:';
        const clean = relayParam.replace(/^https?:\/\//, '').replace(/\/+$/, '');
        return inviteParam
          ? `${wsProto}//${clean}/ws?invite=${encodeURIComponent(inviteParam)}`
          : `${wsProto}//${clean}/ws`;
      }
      const storedGw = typeof window !== 'undefined' ? sessionStorage.getItem('msng3r_gateway_url') : null;
      if (storedGw) {
        const raw = storedGw.trim();
        if (raw.startsWith('ws://') || raw.startsWith('wss://')) {
          return raw.endsWith('/ws') ? raw : `${raw.replace(/\/+$/, '')}/ws`;
        }
        const proto = raw.startsWith('https:') ? 'wss:' : 'ws:';
        const clean = raw.replace(/^https?:\/\//, '');
        return `${proto}//${clean}${clean.endsWith('/ws') ? '' : '/ws'}`;
      }
      if (typeof window !== 'undefined' && (window as any).MSNG3R_ENV?.defaultGatewayUrl) {
        const raw = String((window as any).MSNG3R_ENV.defaultGatewayUrl).trim();
        if (raw.startsWith('ws://') || raw.startsWith('wss://')) {
          return raw.endsWith('/ws') ? raw : `${raw.replace(/\/+$/, '')}/ws`;
        }
        const proto = raw.startsWith('https:') ? 'wss:' : 'ws:';
        const clean = raw.replace(/^https?:\/\//, '');
        return `${proto}//${clean}${clean.endsWith('/ws') ? '' : '/ws'}`;
      }
      if (typeof window !== 'undefined' && window.location.protocol === 'app:') {
        return 'wss://relay.msng3r.com/ws';
      }
      const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
      const wsProtocol = isHttps ? 'wss:' : 'ws:';
      const hostname = typeof window !== 'undefined' ? window.location.hostname || 'localhost' : 'localhost';
      const host = typeof window !== 'undefined' ? window.location.host : `${hostname}:8080`;
      return `${wsProtocol}//${host}/ws`;
    };

    const wsUrl = getSignalingEndpoint();
    const link = new WebSocketLinkAdapter(wsUrl);
    link.onReceive = async (env) => {
      await bus.receive(env);
    };
    bus.registerLink(link);

    // Register BroadcastChannel link for instantaneous inter-tab mesh synchronization
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const bcLink = new BroadcastChannelLink('sovereign-mesh-inter-tab', async (env) => {
          await bus.receive(env);
        });
        bus.registerLink(bcLink);
      } catch (err) {
        console.warn('BroadcastChannelLink setup skipped:', err);
      }
    }

    const bcast = new BroadcastService(nodeId, bus);
    const dir = new DirectoryService(nodeId, bcast);
    const inv = new InviteService(nodeId, dir, bcast);
    const ingestion = new ChatIngestionService(bcast);

    return { directory: dir, broadcast: bcast, invite: inv, ingestion };
  }, [nodeId]);

  useEffect(() => {
    // Start singleton background ingestion
    ingestion.start({
      meshId: 'mesh-sovereign-alpha',
      workspaceId: 'ws-default',
      localNodeId: nodeId,
    });

    return () => {
      ingestion.stop();
    };
  }, [ingestion, nodeId]);

  useEffect(() => {
    const getProfile = () => {
      let displayName = `Node (${nodeId.substring(0, 10)})`;
      let hnsHandle = undefined;
      
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem(`profile_data_${nodeId}`);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed?.displayName) displayName = parsed.displayName;
            if (parsed?.hnsId || parsed?.hnsAlias) hnsHandle = parsed.hnsId || parsed.hnsAlias;
          } catch {}
        }
      }
      return { displayName, hnsHandle };
    };

    // Record local heartbeat
    const initialProfile = getProfile();
    directory.recordHeartbeat({
      nodeId,
      displayName: initialProfile.displayName,
      hnsHandle: initialProfile.hnsHandle,
      lastSeen: Date.now(),
      status: 'online',
      links: ['broadcast_channel'],
    });

    // Start periodic heartbeat ticker
    const interval = setInterval(() => {
      const p = getProfile();
      directory.recordHeartbeat({
        nodeId,
        displayName: p.displayName,
        hnsHandle: p.hnsHandle,
        lastSeen: Date.now(),
        status: 'online',
        links: ['broadcast_channel'],
      });
      directory.pruneExpiredPeers(Date.now(), 15000);
    }, 5000);

    return () => clearInterval(interval);
  }, [nodeId, directory]);

  return (
    <AppShell
      nodeId={nodeId}
      directory={directory}
      broadcast={broadcast}
      invite={invite}
    />
  );
};
